/**
 * formThemes.ts — Theme presets and types for the dynamic form system.
 *
 * Each theme defines colors, typography, and optional header imagery
 * that can be applied to DynamicFormBuilder / DynamicSection / DynamicFormModal.
 *
 * The DB columns `theme_config` and `branding_config` on `form_configurations`
 * persist the chosen values. These presets give users a quick-start palette.
 *
 * Updated: February 2026
 */

// ── Theme Types ─────────────────────────────────────────────────

export interface FormThemeConfig {
  /** Page / modal background */
  bg_color: string;
  /** Section card background */
  card_color: string;
  /** Primary accent (buttons, progress bars, header strip) */
  accent_color: string;
  /** Font family stack */
  font_family: string;
  /** Section title / question label color */
  question_color: string;
  /** Field value / answer text color */
  answer_color: string;
  /** Optional header gradient (CSS linear-gradient value) */
  header_gradient?: string;
  /** Optional header background image URL */
  header_image_url?: string;
  /** Border radius for section cards (px) */
  border_radius?: number;
  /** Section header background (if different from card_color) */
  section_header_bg?: string;
  /** Input / field border color */
  input_border_color?: string;
  /** Input background color */
  input_bg_color?: string;

  // ── Phase 5 additions ──────────────────────────────
  /** Background pattern overlay key (e.g. 'dots', 'diagonal-lines') */
  bg_pattern?: string;
  /** Background image URL (full page, not just header) */
  bg_image_url?: string;
  /** Background image opacity 0–1 (shown as "Brightness" in the editor) */
  bg_image_opacity?: number;

  // ── UI/UX parity additions ─────────────────────────
  /** Corner style preset — maps onto border_radius (sharp=4, rounded=12, pill=24) */
  corner_style?: 'sharp' | 'rounded' | 'pill';
  /** Global text-size scale for the rendered form */
  text_size?: 'default' | 'large' | 'xl';
  /** Background image fit mode */
  bg_image_fit?: 'cover' | 'repeat' | 'contain';
  /** CSS background-position for the background image */
  bg_image_position?: string;
  /** Section/card border color (defaults to a subtle tone derived from theme) */
  card_border_color?: string;
  /** Sanitized custom CSS injected only in the public renderer + preview */
  custom_css?: string;
}

export interface FormBrandingConfig {
  show_branding: boolean;
  watermark_text: string;
  brand_color: string;
  brand_logo_url?: string;
  header_image_url?: string;
}

export interface FormThemePreset {
  id: string;
  name: string;
  description: string;
  /** Preview swatch colors shown in the picker */
  swatches: [string, string, string];
  theme: FormThemeConfig;
}

// ── Default / Fallback ──────────────────────────────────────────

export const DEFAULT_FORM_THEME: FormThemeConfig = {
  bg_color: '#f8fafc',
  card_color: '#ffffff',
  accent_color: '#14b8a6',
  font_family: 'Inter, system-ui, sans-serif',
  question_color: '#1e293b',
  answer_color: '#334155',
  border_radius: 12,
  section_header_bg: '#ffffff',
  input_border_color: '#e2e8f0',
  input_bg_color: '#ffffff',
  text_size: 'default',
  bg_image_fit: 'cover',
  bg_image_position: 'center center',
  bg_image_opacity: 0.15,
};

// ── Theme Presets ───────────────────────────────────────────────

export const FORM_THEME_PRESETS: FormThemePreset[] = [
  {
    id: 'default',
    name: 'Clean Slate',
    description: 'Minimal white with teal accents',
    swatches: ['#f8fafc', '#14b8a6', '#1e293b'],
    theme: { ...DEFAULT_FORM_THEME },
  },
  {
    id: 'modern-blue',
    name: 'Modern Blue',
    description: 'Cool blue tones with a professional edge',
    swatches: ['#eff6ff', '#3b82f6', '#1e40af'],
    theme: {
      bg_color: '#eff6ff',
      card_color: '#ffffff',
      accent_color: '#3b82f6',
      font_family: 'Inter, system-ui, sans-serif',
      question_color: '#1e40af',
      answer_color: '#334155',
      header_gradient: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
      border_radius: 12,
      section_header_bg: '#f0f7ff',
      input_border_color: '#bfdbfe',
      input_bg_color: '#ffffff',
    },
  },
  {
    id: 'dark-slate',
    name: 'Dark Slate',
    description: 'Dark mode with elegant grays',
    swatches: ['#0f172a', '#1e293b', '#38bdf8'],
    theme: {
      bg_color: '#0f172a',
      card_color: '#1e293b',
      accent_color: '#38bdf8',
      font_family: 'Inter, system-ui, sans-serif',
      question_color: '#e2e8f0',
      answer_color: '#94a3b8',
      header_gradient: 'linear-gradient(135deg, #0ea5e9, #2563eb)',
      border_radius: 12,
      section_header_bg: '#1e293b',
      input_border_color: '#334155',
      input_bg_color: '#0f172a',
    },
  },
  {
    id: 'emerald',
    name: 'Emerald Garden',
    description: 'Fresh green tones for eco & compliance forms',
    swatches: ['#f0fdf4', '#10b981', '#065f46'],
    theme: {
      bg_color: '#f0fdf4',
      card_color: '#ffffff',
      accent_color: '#10b981',
      font_family: 'Inter, system-ui, sans-serif',
      question_color: '#065f46',
      answer_color: '#374151',
      header_gradient: 'linear-gradient(135deg, #10b981, #059669)',
      border_radius: 14,
      section_header_bg: '#ecfdf5',
      input_border_color: '#a7f3d0',
      input_bg_color: '#ffffff',
    },
  },
  {
    id: 'sunset',
    name: 'Sunset Warm',
    description: 'Warm orange & amber tones',
    swatches: ['#fff7ed', '#f97316', '#9a3412'],
    theme: {
      bg_color: '#fff7ed',
      card_color: '#ffffff',
      accent_color: '#f97316',
      font_family: 'Inter, system-ui, sans-serif',
      question_color: '#9a3412',
      answer_color: '#431407',
      header_gradient: 'linear-gradient(135deg, #f97316, #ea580c)',
      border_radius: 12,
      section_header_bg: '#fff7ed',
      input_border_color: '#fed7aa',
      input_bg_color: '#ffffff',
    },
  },
  {
    id: 'ocean',
    name: 'Ocean Depth',
    description: 'Deep blue-indigo gradients',
    swatches: ['#eef2ff', '#6366f1', '#312e81'],
    theme: {
      bg_color: '#eef2ff',
      card_color: '#ffffff',
      accent_color: '#6366f1',
      font_family: 'Inter, system-ui, sans-serif',
      question_color: '#312e81',
      answer_color: '#4338ca',
      header_gradient: 'linear-gradient(135deg, #6366f1, #4f46e5)',
      border_radius: 16,
      section_header_bg: '#e8ecff',
      input_border_color: '#c7d2fe',
      input_bg_color: '#ffffff',
    },
  },
  {
    id: 'corporate',
    name: 'Corporate',
    description: 'Neutral grays with sharp corners',
    swatches: ['#fafafa', '#18181b', '#71717a'],
    theme: {
      bg_color: '#fafafa',
      card_color: '#ffffff',
      accent_color: '#18181b',
      font_family: '"Geist", "Helvetica Neue", Arial, sans-serif',
      question_color: '#18181b',
      answer_color: '#3f3f46',
      header_gradient: 'linear-gradient(135deg, #27272a, #18181b)',
      border_radius: 6,
      section_header_bg: '#fafafa',
      input_border_color: '#d4d4d8',
      input_bg_color: '#ffffff',
    },
  },
  {
    id: 'rose',
    name: 'Rose Petal',
    description: 'Soft pink & rose tones',
    swatches: ['#fff1f2', '#f43f5e', '#881337'],
    theme: {
      bg_color: '#fff1f2',
      card_color: '#ffffff',
      accent_color: '#f43f5e',
      font_family: 'Inter, system-ui, sans-serif',
      question_color: '#881337',
      answer_color: '#4c0519',
      header_gradient: 'linear-gradient(135deg, #f43f5e, #e11d48)',
      border_radius: 14,
      section_header_bg: '#ffe4e6',
      input_border_color: '#fecdd3',
      input_bg_color: '#ffffff',
    },
  },
  {
    id: 'electric',
    name: 'Electric Neon',
    description: 'High-contrast dark with neon accents',
    swatches: ['#18181b', '#a855f7', '#f0abfc'],
    theme: {
      bg_color: '#18181b',
      card_color: '#27272a',
      accent_color: '#a855f7',
      font_family: '"JetBrains Mono", "Fira Code", monospace',
      question_color: '#f0abfc',
      answer_color: '#d4d4d8',
      header_gradient: 'linear-gradient(135deg, #a855f7, #7c3aed)',
      border_radius: 10,
      section_header_bg: '#27272a',
      input_border_color: '#3f3f46',
      input_bg_color: '#18181b',
    },
  },
  {
    id: 'nature',
    name: 'Forest Canopy',
    description: 'Deep greens with earthy browns',
    swatches: ['#f5f5f4', '#166534', '#a16207'],
    theme: {
      bg_color: '#f5f5f4',
      card_color: '#ffffff',
      accent_color: '#166534',
      font_family: 'Inter, system-ui, sans-serif',
      question_color: '#14532d',
      answer_color: '#374151',
      header_gradient: 'linear-gradient(135deg, #166534, #15803d)',
      border_radius: 12,
      section_header_bg: '#f0fdf4',
      input_border_color: '#bbf7d0',
      input_bg_color: '#ffffff',
    },
  },
];

// ── Helpers ─────────────────────────────────────────────────────

/** Merge partial theme_config from DB with safe defaults */
export function resolveFormTheme(partial?: Partial<FormThemeConfig> | null): FormThemeConfig {
  if (!partial) return { ...DEFAULT_FORM_THEME };
  const merged = { ...DEFAULT_FORM_THEME, ...partial };
  // Corner style presets override numeric radius when present.
  if (partial.corner_style) {
    if (partial.corner_style === 'sharp') merged.border_radius = 4;
    if (partial.corner_style === 'rounded') merged.border_radius = 12;
    if (partial.corner_style === 'pill') merged.border_radius = 24;
  }
  return merged;
}

/** Find a preset by id */
export function getFormThemePreset(presetId: string): FormThemePreset | undefined {
  return FORM_THEME_PRESETS.find(p => p.id === presetId);
}

/** Check if background is dark (for auto-text-color logic) */
export function isThemeDark(theme: FormThemeConfig): boolean {
  const hex = theme.bg_color?.replace('#', '') || 'f8fafc';
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}

/**
 * Build a dark-mode override for a form theme.
 * Preserves accent, radius, and typography; only flips palette-neutral colors.
 */
export function buildFormThemeForMode(
  partial: Partial<FormThemeConfig> | null | undefined,
  dark: boolean,
): Partial<FormThemeConfig> {
  if (!partial || !dark) return { ...partial } as Partial<FormThemeConfig>;
  const resolved = resolveFormTheme(partial);
  if (isThemeDark(resolved)) return { ...partial } as Partial<FormThemeConfig>;
  return {
    ...partial,
    bg_color: '#0f172a',
    card_color: '#1e293b',
    section_header_bg: '#334155',
    question_color: '#f8fafc',
    answer_color: '#e2e8f0',
    input_bg_color: '#0f172a',
    input_border_color: '#475569',
  };
}

// ── Background Patterns ─────────────────────────────────────────

export interface BgPatternPreset {
  id: string;
  name: string;
  /** CSS background-image for the pattern overlay */
  css: string;
}

/**
 * SVG-based CSS background patterns.
 * Each uses an inline SVG data URI so no external assets are needed.
 * The SVG strokes/fills use a 20 % opacity black for light themes;
 * the consumer can layer them with mix-blend-mode or opacity.
 */
export const BG_PATTERN_PRESETS: BgPatternPreset[] = [
  {
    id: 'dots',
    name: 'Dots Grid',
    css: `url("data:image/svg+xml,%3Csvg width='20' height='20' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='10' cy='10' r='1.2' fill='%23000' fill-opacity='0.07'/%3E%3C/svg%3E")`,
  },
  {
    id: 'diagonal-lines',
    name: 'Diagonal Lines',
    css: `url("data:image/svg+xml,%3Csvg width='12' height='12' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 12L12 0M-3 3L3 -3M9 15L15 9' stroke='%23000' stroke-opacity='0.06' stroke-width='1'/%3E%3C/svg%3E")`,
  },
  {
    id: 'waves',
    name: 'Subtle Waves',
    css: `url("data:image/svg+xml,%3Csvg width='100' height='20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 10 Q25 0 50 10 T100 10' fill='none' stroke='%23000' stroke-opacity='0.05' stroke-width='1'/%3E%3C/svg%3E")`,
  },
  {
    id: 'circuit',
    name: 'Circuit Board',
    css: `url("data:image/svg+xml,%3Csvg width='40' height='40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 20h15v-10h10v20h15M20 0v10M20 30v10' fill='none' stroke='%23000' stroke-opacity='0.05' stroke-width='0.8'/%3E%3Ccircle cx='20' cy='20' r='2' fill='%23000' fill-opacity='0.06'/%3E%3C/svg%3E")`,
  },
  {
    id: 'topography',
    name: 'Topography',
    css: `url("data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5c14 0 25 11 25 25S44 55 30 55 5 44 5 30 16 5 30 5zM30 15c8 0 15 7 15 15s-7 15-15 15-15-7-15-15 7-15 15-15z' fill='none' stroke='%23000' stroke-opacity='0.04' stroke-width='0.8'/%3E%3C/svg%3E")`,
  },
  {
    id: 'geometric',
    name: 'Geometric',
    css: `url("data:image/svg+xml,%3Csvg width='30' height='30' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='2' y='2' width='12' height='12' rx='2' fill='none' stroke='%23000' stroke-opacity='0.05' stroke-width='0.8'/%3E%3C/svg%3E")`,
  },
  {
    id: 'cross-hatch',
    name: 'Cross Hatch',
    css: `url("data:image/svg+xml,%3Csvg width='16' height='16' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 8h16M8 0v16' stroke='%23000' stroke-opacity='0.04' stroke-width='0.5'/%3E%3C/svg%3E")`,
  },
];

/**
 * Build inline CSS for the background pattern overlay.
 * Returns an object that can be spread onto a style prop.
 */
export function resolvePatternCSS(theme: FormThemeConfig): React.CSSProperties | undefined {
  if (!theme.bg_pattern) return undefined;
  const pat = BG_PATTERN_PRESETS.find(p => p.id === theme.bg_pattern);
  if (!pat) return undefined;
  return { backgroundImage: pat.css, backgroundRepeat: 'repeat' };
}

/**
 * Build a scoped stylesheet that pushes the resolved theme into field-level
 * elements (inputs, selects, textareas, labels, checkable accents). Renderers
 * share the section/card theming already, but DynamicField uses portal theme
 * classes — this generated CSS is what makes the live form match the designer
 * canvas (input_bg_color, input_border_color, question/answer colors, accent).
 *
 * Generated output is trusted (no sanitize needed); callers wrap it in a
 * <style> tag and put `scopeClass` on the form container. `!important` is
 * required to beat portal dark-variant utility classes on inputs.
 */
export function buildFormThemeCss(theme: FormThemeConfig, scopeClass: string): string {
  const s = `.${scopeClass}`;
  const inputBg = theme.input_bg_color || '#ffffff';
  const inputBorder = theme.input_border_color || '#e2e8f0';
  const textInputSel =
    `${s} input:not([type="checkbox"]):not([type="radio"]):not([type="range"])` +
    `:not([type="color"]):not([type="button"]):not([type="submit"])` +
    `:not([type="file"]):not([type="hidden"]):not([type="image"])`;

  return [
    // Native widget color-scheme so date pickers, select carets and scrollbars
    // render in the right palette for dark themes.
    `${s} { color-scheme: ${isThemeDark(theme) ? 'dark' : 'light'}; }`,
    `${textInputSel}, ${s} select, ${s} textarea {
      background-color: ${inputBg} !important;
      color: ${theme.answer_color} !important;
      border-color: ${inputBorder} !important;
      font-family: inherit;
    }`,
    `${textInputSel}::placeholder, ${s} textarea::placeholder {
      color: ${theme.answer_color} !important;
      opacity: 0.55;
    }`,
    `${s} select option {
      background-color: ${inputBg};
      color: ${theme.answer_color};
    }`,
    // Disabled/readonly inputs (linked work-item fields, locked values) must
    // stay legible — browsers otherwise dim them, and iOS Safari needs the
    // text-fill override.
    `${s} input:disabled, ${s} select:disabled, ${s} textarea:disabled,
     ${s} input[readonly], ${s} textarea[readonly] {
      color: ${theme.answer_color} !important;
      -webkit-text-fill-color: ${theme.answer_color} !important;
      opacity: 1;
    }`,
    `${textInputSel}:focus, ${s} select:focus, ${s} textarea:focus {
      border-color: ${theme.accent_color} !important;
      --tw-ring-color: ${theme.accent_color}66;
    }`,
    `${s} input[type="checkbox"], ${s} input[type="radio"] {
      accent-color: ${theme.accent_color};
    }`,
    // Field labels and option-group legends take the question color.
    `${s} label, ${s} legend {
      color: ${theme.question_color} !important;
    }`,
  ].join('\n');
}
